import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, ThemeMenu } from '@easydent/shared-ui';
import { resetPassword } from '@/lib/api';

/**
 * Sets a password from an emailed link.
 *
 * <p>Serves two situations that are the same operation: someone who forgot their password, and a
 * newly created operator setting one for the first time. That is why creating an operator needs no
 * accept-invite screen of its own.</p>
 *
 * <p>Unauthenticated by design — the token in the URL is the proof. It is single-use and short-lived,
 * and a wrong or expired one is told apart from a rejected password, because "try again" and "ask for
 * a new link" are different actions and a single "something went wrong" leaves people stuck.</p>
 */
const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError(t('reset.mismatch', 'The two passwords do not match.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      // 404 means the link itself is spent or expired; 400 means the password was refused. Only one
      // of those is fixed by typing something different.
      if (status === 404) setExpired(true);
      else setError(
        (err as { response?: { data?: { title?: string; detail?: string } } })?.response?.data?.detail
        ?? (err as { response?: { data?: { title?: string } } })?.response?.data?.title
        ?? t('reset.failed', 'That password was not accepted.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:border-brand-500 focus:outline-none';

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-8 shadow-sm">
        <div className="mb-4 flex justify-end">
          <ThemeMenu labels={{ label: t('theme.label', 'Theme'), light: t('theme.light', 'Light'), dark: t('theme.dark', 'Dark'), system: t('theme.system', 'System') }} />
        </div>
        <div className="mb-6 flex items-center gap-2 text-xl font-bold text-brand-ink-strong">
          <span>⬡</span> easyDent <span className="font-normal text-subtle-ink">{t('portalName', 'Operator')}</span>
        </div>

        {done ? (
          <Alert variant="success">
            {t('reset.done', 'Your password is set. Taking you to sign in…')}
          </Alert>
        ) : !token || expired ? (
          <>
            <Alert variant="error" className="mb-4">
              {t('reset.expired', 'This link has already been used or has expired.')}
            </Alert>
            <Link to="/forgot-password" className="text-sm font-medium text-brand-ink-strong hover:underline">
              {t('reset.askAgain', 'Ask for a new link')}
            </Link>
          </>
        ) : (
          <form onSubmit={submit}>
            <p className="mb-5 text-sm text-muted">
              {t('reset.intro', 'Choose a new password for your operator account.')}
            </p>
            {error && <Alert variant="error" className="mb-3">{error}</Alert>}

            <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">
              {t('reset.newPassword', 'New password')}
            </label>
            <input
              type="password" autoComplete="new-password" autoFocus required minLength={10}
              className={`${field} mb-1`} value={password} onChange={(e) => setPassword(e.target.value)}
            />
            <p className="mb-3 text-xs text-subtle-ink">
              {t('reset.policy', 'At least 10 characters, including an upper-case letter, a lower-case letter, a number and a symbol.')}
            </p>

            <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">
              {t('reset.confirm', 'Confirm password')}
            </label>
            <input
              type="password" autoComplete="new-password" required minLength={10}
              className={`${field} mb-5`} value={confirm} onChange={(e) => setConfirm(e.target.value)}
            />

            <Button type="submit" disabled={busy} className="w-full">
              {busy ? t('reset.saving', 'Saving…') : t('reset.submit', 'Set password')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
